import { useCallback, useEffect, useRef, useState } from "react";
import {
  listTickets, getTicket, replyTicket, updateTicket, attachmentURL, eventsURL,
  type SupportTicket,
} from "../api/support";

const PRIO_LABEL: Record<string, string> = { low: "Düşük", normal: "Normal", high: "Yüksek" };

// Admin (superadmin) perspektifi: son yanıtlayan destekse "Yanıtlandı",
// müşteriyse "Cevabınız bekleniyor" (destekten yanıt bekleniyor).
function statusInfo(t: SupportTicket): { label: string; cls: string } {
  if (t.status === "closed") return { label: "Kapalı", cls: "off" };
  if (t.last_author_role === "superadmin") return { label: "Yanıtlandı", cls: "ok" };
  return { label: "Cevabınız bekleniyor", cls: "warn" };
}

function fmt(s: string): string { try { return new Date(s).toLocaleString(); } catch { return s; } }

export default function Support() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState<SupportTicket | null>(null);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [replying, setReplying] = useState(false);
  const activeIdRef = useRef<number | null>(null);
  activeIdRef.current = active?.id ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try { setTickets(await listTickets(filter)); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [filter]);

  const reloadActive = useCallback(async (id: number) => {
    try { setActive(await getTicket(id)); } catch { /* sessiz */ }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Canlı SSE
  useEffect(() => {
    const es = new EventSource(eventsURL());
    const onEvt = (ev: MessageEvent) => {
      void load();
      try {
        const data = JSON.parse(ev.data) as { ticket_id: number };
        if (activeIdRef.current && data.ticket_id === activeIdRef.current) void reloadActive(activeIdRef.current);
      } catch { /* ignore */ }
    };
    es.addEventListener("ticket_created", onEvt);
    es.addEventListener("message_added", onEvt);
    es.addEventListener("ticket_updated", onEvt);
    return () => es.close();
  }, [load, reloadActive]);

  const open = useCallback(async (id: number) => {
    setErr(null);
    try { setActive(await getTicket(id)); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }, []);

  const onlyPdf = (l: FileList | null): File[] =>
    l ? Array.from(l).filter((f) => f.name.toLowerCase().endsWith(".pdf")).slice(0, 5) : [];

  const onReply = useCallback(async () => {
    if (!active || !reply.trim()) return;
    setReplying(true);
    setErr(null);
    try {
      const u = await replyTicket(active.id, reply.trim(), replyFiles);
      setActive(u); setReply(""); setReplyFiles([]);
      void load();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setReplying(false); }
  }, [active, reply, replyFiles, load]);

  const onPatch = useCallback(async (patch: { status?: string; priority?: string }) => {
    if (!active) return;
    setErr(null);
    try { setActive(await updateTicket(active.id, patch)); void load(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }, [active, load]);

  // ---- Detail ----
  if (active) {
    return (
      <div className="page sup sup-detail-view">
        <button className="sup-back" onClick={() => setActive(null)}>← Listeye dön</button>
        <div className="sup-detail-head">
          <div>
            <div className="sup-detail-id">#{active.id} · {active.org_name}</div>
            <h2 className="sup-title">{active.subject}</h2>
            <div className="sup-card-meta">{active.creator_name || active.creator_email} · {fmt(active.created_at)}</div>
          </div>
          <span className={"badge " + statusInfo(active).cls}>{statusInfo(active).label}</span>
        </div>
        {err && <div className="error">{err}</div>}

        <div className="sup-controls">
          <label>Durum
            <select value={active.status} onChange={(e) => onPatch({ status: e.target.value })}>
              <option value="open">Açık</option><option value="answered">Yanıtlandı</option><option value="closed">Kapalı</option>
            </select>
          </label>
          <label>Öncelik
            <select value={active.priority} onChange={(e) => onPatch({ priority: e.target.value })}>
              <option value="low">Düşük</option><option value="normal">Normal</option><option value="high">Yüksek</option>
            </select>
          </label>
        </div>

        <div className="sup-thread">
          {(active.messages ?? []).map((m) => {
            const admin = m.author_role === "superadmin";
            return (
              <div key={m.id} className={"sup-msg " + (admin ? "sup-msg-admin" : "sup-msg-client")}>
                <div className="sup-msg-head">
                  <strong>{admin ? "Destek Ekibi" : (m.author_name || "Müşteri")}</strong>
                  <span>{fmt(m.created_at)}</span>
                </div>
                <div className="sup-msg-body">{m.body}</div>
                {(m.attachments ?? []).length > 0 && (
                  <div className="sup-files">
                    {m.attachments!.map((a) => (
                      <a key={a.id} href={attachmentURL(a.id)} target="_blank" rel="noreferrer" className="sup-chip sup-chip-link">
                        📎 {a.file_name} <em>({(a.size_bytes / 1024).toFixed(0)} KB)</em>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="sup-detail-foot">
          <div className="sup-composer">
            <textarea rows={2} value={reply} placeholder="Yanıtınızı yazın…" onChange={(e) => setReply(e.target.value)} />
            <div className="sup-composer-bar">
              <label className="sup-attach-btn">📎 PDF ekle
                <input type="file" accept="application/pdf,.pdf" multiple onChange={(e) => setReplyFiles(onlyPdf(e.target.files))} />
              </label>
              {replyFiles.length > 0 && <span className="sup-files">{replyFiles.map((f) => <span key={f.name} className="sup-chip">{f.name}</span>)}</span>}
              <button className="sup-btn sup-btn-primary" style={{ marginLeft: "auto" }} disabled={replying || !reply.trim()} onClick={onReply}>
                {replying ? "Gönderiliyor…" : "Yanıtla"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- List ----
  return (
    <div className="page sup">
      <div className="sup-head">
        <div>
          <h2 className="sup-title">Destek Talepleri</h2>
          <p className="sup-sub">Müşterilerin açtığı talepler — anlık güncellenir.</p>
        </div>
        <label className="sup-filter">Durum
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">Hepsi</option><option value="open">Cevap bekleyen</option>
            <option value="answered">Yanıtlanan</option><option value="closed">Kapalı</option>
          </select>
        </label>
      </div>
      {err && <div className="error">{err}</div>}
      {loading && <div className="sup-empty">Yükleniyor…</div>}
      {!loading && tickets.length === 0 && (
        <div className="sup-empty"><div className="sup-empty-ico">🎧</div><p>Talep yok.</p></div>
      )}
      <div className="sup-list">
        {tickets.map((t) => {
          const si = statusInfo(t);
          return (
          <button key={t.id} className={"sup-card sup-card-" + si.cls} onClick={() => open(t.id)}>
            <div className="sup-card-main">
              <div className="sup-card-subject">{t.subject}</div>
              <div className="sup-card-meta">
                <span>#{t.id}</span><span>· {t.org_name}</span><span>· {PRIO_LABEL[t.priority]}</span>
                <span>· 💬 {t.message_count ?? 0}</span><span>· {fmt(t.updated_at)}</span>
              </div>
            </div>
            <span className={"badge " + si.cls}>{si.label}</span>
          </button>
          );
        })}
      </div>
    </div>
  );
}
